/**
 * Stats Store
 * Zustand store for user statistics with localStorage persistence
 * Maintains backward compatibility with vanilla JS localStorage keys
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Stats } from '../types'
import { STORAGE_KEYS } from '@config/constants'

interface StatsStore {
  stats: Stats
  incrementSolved: () => void
  updateBestStreak: (streak: number) => void
  resetStats: () => void
}

const DEFAULT_STATS: Stats = {
  solved: 0,
  bestStreak: 0,
}

function sanitizeLoadedStats(stored: unknown): Stats {
  if (!stored || typeof stored !== 'object') {
    return { ...DEFAULT_STATS }
  }

  const parsed = stored as Partial<Stats>

  return {
    solved: Number.isFinite(parsed.solved) ? (parsed.solved as number) : 0,
    bestStreak: Number.isFinite(parsed.bestStreak) ? (parsed.bestStreak as number) : 0,
  }
}

export const useStatsStore = create<StatsStore>()(
  persist(
    (set) => ({
      stats: { ...DEFAULT_STATS },

      incrementSolved: () =>
        set((state) => ({
          stats: {
            ...state.stats,
            solved: state.stats.solved + 1,
          },
        })),

      updateBestStreak: (streak) =>
        set((state) => ({
          stats: {
            ...state.stats,
            bestStreak: Math.max(state.stats.bestStreak, streak),
          },
        })),

      resetStats: () =>
        set({
          stats: { ...DEFAULT_STATS },
        }),
    }),
    {
      name: STORAGE_KEYS.score,
      partialize: (state) => ({ stats: state.stats }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Sanitize stats on load
          const stored = localStorage.getItem(STORAGE_KEYS.score)
          if (stored) {
            try {
              const parsed = JSON.parse(stored)
              const sanitized = sanitizeLoadedStats(parsed.state?.stats || parsed)
              state.stats = sanitized
            } catch (error) {
              console.warn('Failed to parse stored stats:', error)
            }
          }
        }
      },
    }
  )
)
